import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import GoogleWatchdogService from 'services/GoogleWatchdogService';
import { PerformanceRow } from '../types';
import { currencyPrefix } from '../logic';
import { buttonGroup, plainButton, pressable } from '../controls';
import {
  COLUMN_BY_KEY,
  COLUMN_PREFS_KEY,
  DATE_PRESETS,
  DEFAULT_COLUMNS,
  DEFAULT_PRESET,
  DEFAULT_SORT,
  DatePreset,
  DateRange,
  PERF_COLUMNS,
  PerfColumn,
  PerfNode,
  PerfSort,
  PerfSource,
  SOURCE_LABEL,
  buildTree,
  formatPerf,
  parseColumnPrefs,
  presetRange,
  rangeWords,
  sortTree,
  sumTotals,
  todayPT,
  applyRenewals,
  hasRenewals,
} from '../performance';

const service = new GoogleWatchdogService();

/** The page's My campaigns filter, while it is on. */
export interface MineFilter {
  ids: Set<string>;
  /** How many of them are in the stores in view; null until the campaign list has loaded. */
  inView: number | null;
  /** The brand and store in view, in words: "Araluna", "Dosaze UK". */
  scope: string;
}

interface SharedProps {
  /** The stores in view: the brand tab, narrowed by the store chip. */
  stores: string[];
  /** Show a store tag on each campaign, when the view spans more than one store. */
  showStore: boolean;
  now: Date;
}
/** The page's table: every campaign in the stores in view, or the viewer's while My campaigns is on. */
interface PageProps extends SharedProps {
  mine: MineFilter | null;
  campaignId?: never;
  windows?: never;
}
/**
 * Just this campaign, shown inside a Watchdog card's row: date buttons and the table, with the
 * columns chosen on the page table, and none of the page table's other controls.
 */
interface RowProps extends SharedProps {
  campaignId: string;
  /** The periods behind the card's before and after figure; null when the card found no change day. */
  windows: { before: DateRange; after: DateRange } | null;
  mine?: never;
}
type Props = PageProps | RowProps;
type RangeKey = DatePreset | 'before' | 'after';

/** "None of your 4 Araluna campaigns spent between 24 and 30 Sep." */
function mineEmptyNote(mine: MineFilter, range: DateRange): string {
  const when = range.from === range.to ? `on ${rangeWords(range, 'and')}` : `between ${rangeWords(range, 'and')}`;
  if (mine.inView === 1) return `Your ${mine.scope} campaign didn't spend ${when}.`;
  return `None of your ${mine.inView == null ? '' : `${mine.inView} `}${mine.scope} campaigns spent ${when}.`;
}

const dateInput = {
  font: 'inherit',
  fontSize: '0.8125rem',
  color: '#0F172A',
  border: '1px solid #CBD5E1',
  borderRadius: '6px',
  px: '8px',
  py: '5px',
  bgcolor: '#FFFFFF',
} as const;
const RULE = '1px solid #E2E8F0';
const INDENT: Record<PerfNode['level'], number> = {
  campaign: 0,
  adset: 22,
  ad: 44,
};

function readColumns(): string[] {
  try {
    return (
      parseColumnPrefs(localStorage.getItem(COLUMN_PREFS_KEY)) ??
      DEFAULT_COLUMNS
    );
  } catch {
    return DEFAULT_COLUMNS;
  }
}
function saveColumns(keys: string[]): void {
  try {
    localStorage.setItem(COLUMN_PREFS_KEY, JSON.stringify(keys));
  } catch {
    // Private windows and blocked storage: the choice just lasts for this visit.
  }
}

// One request per stores and dates, shared by the page table and every opened card row, so
// Expand all on a page of cards asks once rather than once per card. Kept five minutes; a failed
// request is forgotten at once so Retry asks again.
const CACHE_MS = 5 * 60 * 1000;
const requests = new Map<string, { at: number; promise: Promise<PerformanceRow[]> }>();
function fetchPerformance(stores: string[], from: string, to: string): Promise<PerformanceRow[]> {
  const key = `${stores.join(',')}|${from}|${to}`;
  const hit = requests.get(key);
  if (hit && Date.now() - hit.at < CACHE_MS) return hit.promise;
  const promise = service.performance({ stores, from, to });
  requests.set(key, { at: Date.now(), promise });
  promise.catch(() => requests.delete(key));
  return promise;
}

function ColumnPicker({
  open,
  columns,
  onClose,
  onChange,
}: {
  open: boolean;
  columns: string[];
  onClose: () => void;
  onChange: (keys: string[]) => void;
}) {
  const move = (index: number, delta: number) => {
    const next = [...columns];
    const [key] = next.splice(index, 1);
    next.splice(index + delta, 0, key);
    onChange(next);
  };
  const hidden = PERF_COLUMNS.filter(column => !columns.includes(column.key));
  const sources: PerfSource[] = ['google', 'triple_whale'];
  return (
    // Keeps the page's shortcuts (J/K, C claim, X dismiss, Enter open) from acting on the list behind it.
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth onKeyDown={e => e.stopPropagation()}>
      <DialogTitle>Columns</DialogTitle>
      <DialogContent dividers>
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#64748B',
            mb: 1,
          }}
        >
          Shown, in order
        </Typography>
        {columns.map((key, index) => {
          const column = COLUMN_BY_KEY.get(key) as PerfColumn;
          return (
            <Box
              key={key}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                  {column.label}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
                  {SOURCE_LABEL[column.source]} · {column.description}
                </Typography>
              </Box>
              <IconButton
                size="small"
                aria-label={`Move ${column.label} left`}
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label={`Move ${column.label} right`}
                disabled={index === columns.length - 1}
                onClick={() => move(index, 1)}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label={`Hide ${column.label}`}
                disabled={columns.length === 1}
                onClick={() => onChange(columns.filter(k => k !== key))}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
        {sources.map(source => {
          const available = hidden.filter(column => column.source === source);
          if (!available.length) return null;
          return (
            <Box key={source} sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#64748B',
                  mb: 0.5,
                }}
              >
                Add from {SOURCE_LABEL[source]}
              </Typography>
              {available.map(column => (
                <Box
                  key={column.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.25,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                      {column.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
                      {column.description}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    aria-label={`Add ${column.label}`}
                    onClick={() => onChange([...columns, column.key])}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onChange(DEFAULT_COLUMNS)}>
          Reset to default
        </Button>
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Google Ads campaigns for the stores in view over a date range, with the columns the viewer
 * picks, following the page's My campaigns filter. Each campaign opens into its ad groups, and
 * each ad group into its ads. Given a campaignId, it is that one campaign inside a Watchdog card's
 * row, opening on the card's after period when it has one, else on the last 7 days.
 */
export default function PerformanceTable({
  stores,
  showStore,
  mine,
  now,
  campaignId,
  windows,
}: Props) {
  const inRow = campaignId != null;
  const [preset, setPreset] = useState<RangeKey>(() =>
    windows ? 'after' : inRow ? 'last7' : DEFAULT_PRESET,
  );
  const today = todayPT(now);
  // Custom's dates, and the card period's while Before or After is pressed, so Custom then starts
  // from whatever range was showing.
  const [custom, setCustom] = useState<DateRange>(
    () => windows?.after ?? presetRange('last7', todayPT(now)),
  );
  const range =
    preset === 'custom' || preset === 'before' || preset === 'after'
      ? custom
      : presetRange(preset, today);
  const pickWindow = (key: 'before' | 'after', window: DateRange) => {
    setPreset(key);
    setCustom(window);
  };
  const [rows, setRows] = useState<PerformanceRow[] | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState<string[]>(readColumns);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [sort, setSort] = useState<PerfSort>(DEFAULT_SORT);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [q, setQ] = useState('');
  const [includeRenewals, setIncludeRenewals] = useState(false);

  const storesKey = stores.join(',');
  const load = useCallback(
    async (signal: { cancelled: boolean }) => {
      if (!storesKey || range.from > range.to) return;
      setLoading(true);
      setError('');
      try {
        const data = await fetchPerformance(storesKey.split(','), range.from, range.to);
        if (!signal.cancelled) setRows(data);
      } catch (e: any) {
        if (!signal.cancelled) setError(e.message);
      } finally {
        if (!signal.cancelled) setLoading(false);
      }
    },
    [storesKey, range.from, range.to],
  );
  useEffect(() => {
    const signal = { cancelled: false };
    load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load]);

  const renewalsToChoose = hasRenewals(rows ?? []);
  const tree = useMemo(
    () => buildTree(applyRenewals(rows ?? [], includeRenewals)),
    [rows, includeRenewals],
  );
  const shown = useMemo(() => {
    const scoped = inRow
      ? tree.filter(node => node.campaignId === campaignId)
      : mine
        ? tree.filter(node => mine.ids.has(node.campaignId))
        : tree;
    const needle = q.trim().toLowerCase();
    const matched = needle ? scoped.filter(node => node.name.toLowerCase().includes(needle)) : scoped;
    return sortTree(matched, sort);
  }, [tree, inRow, campaignId, mine, q, sort]);
  const visibleColumns = columns
    .map(key => COLUMN_BY_KEY.get(key))
    .filter((column): column is PerfColumn => column != null);
  // A total across stores in different currencies would add pounds to dollars, so there is none.
  const currencies = new Set(shown.map(node => currencyPrefix(node.store)));
  // One campaign's total is its own row, so a card's row shows none.
  const total = !inRow && shown.length && currencies.size === 1 ? sumTotals(shown) : null;

  const setColumnsAndSave = (keys: string[]) => {
    setColumns(keys);
    saveColumns(keys);
  };
  const toggle = (id: string) =>
    setExpanded(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const sortBy = (key: string) =>
    setSort(current =>
      current.key === key
        ? { key, dir: current.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: key === 'name' ? 'asc' : 'desc' },
    );

  const renderRow = (node: PerfNode): React.ReactNode => {
    const open = expanded.has(node.id);
    const prefix = currencyPrefix(node.store);
    const canOpen = node.children.length > 0 && node.level !== 'ad';
    return (
      <React.Fragment key={node.id}>
        <Box
          component="tr"
          sx={{
            bgcolor: node.level === 'campaign' ? '#FFFFFF' : '#F8FAFC',
            '&:hover td': { bgcolor: 'rgba(100,116,139,0.06)' },
          }}
        >
          <Box
            component="td"
            sx={{
              position: 'sticky',
              left: 0,
              zIndex: 1,
              bgcolor: 'inherit',
              textAlign: 'left !important',
              maxWidth: 360,
              pl: `${10 + INDENT[node.level]}px !important`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
              }}
            >
              {canOpen ? (
                <Box
                  component="button"
                  type="button"
                  aria-expanded={open}
                  aria-label={`${open ? 'Hide' : 'Show'} ${node.level === 'campaign' ? 'ad groups' : 'ads'} of ${node.name}`}
                  onClick={() => toggle(node.id)}
                  sx={{
                    ...plainButton,
                    display: 'flex',
                    p: 0,
                    border: 0,
                    bgcolor: 'transparent',
                    color: '#64748B',
                    borderRadius: '4px',
                  }}
                >
                  <KeyboardArrowRightIcon
                    aria-hidden="true"
                    sx={{
                      fontSize: 18,
                      transform: open ? 'rotate(90deg)' : 'none',
                      transition: 'transform 150ms',
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ width: 18, flexShrink: 0 }} />
              )}
              {showStore && node.level === 'campaign' && (
                <Box
                  component="span"
                  sx={{
                    flexShrink: 0,
                    color: '#475569',
                    border: '1px solid #CBD5E1',
                    borderRadius: '4px',
                    px: '4px',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                  }}
                >
                  {node.store}
                </Box>
              )}
              <Box
                component="span"
                title={node.name}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: node.level === 'campaign' ? 600 : 400,
                  color: node.level === 'campaign' ? '#0F172A' : '#475569',
                }}
              >
                {node.name}
              </Box>
            </Box>
          </Box>
          {visibleColumns.map(column => (
            <Box component="td" key={column.key}>
              {formatPerf(column.format, column.value(node.totals), prefix)}
            </Box>
          ))}
        </Box>
        {canOpen && open && node.children.map(renderRow)}
      </React.Fragment>
    );
  };

  const headerCell = (
    key: string,
    label: string,
    title: string,
    first = false,
  ) => {
    const active = sort.key === key;
    return (
      <Box
        component="th"
        key={key}
        aria-sort={
          active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'
        }
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: first ? 3 : 2,
          ...(first ? { left: 0 } : {}),
          bgcolor: '#FFFFFF',
          borderBottom: RULE,
          textAlign: first ? 'left !important' : 'right',
        }}
      >
        <Tooltip title={title} placement="top" arrow>
          <Box
            component="button"
            type="button"
            onClick={() => sortBy(key)}
            sx={{
              ...plainButton,
              border: 0,
              bgcolor: 'transparent',
              p: 0,
              color: active ? '#0F172A' : '#64748B',
              fontSize: '0.6875rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
            {active ? (sort.dir === 'desc' ? ' ▼' : ' ▲') : ''}
          </Box>
        </Tooltip>
      </Box>
    );
  };

  const empty = !loading && !error && rows != null && !shown.length;
  return (
    <Box
      component={inRow ? 'div' : 'section'}
      aria-labelledby={inRow ? undefined : 'watchdog-performance'}
      sx={{
        border: RULE,
        borderRadius: '8px',
        bgcolor: '#FFFFFF',
        mb: inRow ? 0 : 3.25,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '10px 14px',
          px: 1.75,
          pt: 1.75,
          pb: 1.25,
        }}
      >
        {!inRow && (<>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: '4px 12px',
            mr: 'auto',
          }}
        >
          <Typography
            component="h2"
            id="watchdog-performance"
            sx={{ fontSize: '1.0625rem', fontWeight: 700, lineHeight: 1.2 }}
          >
            Campaign performance
          </Typography>
          <Typography sx={{ color: '#475569', fontSize: '0.8125rem' }}>
            Google Ads · revenue excludes sales tax
            {renewalsToChoose &&
              (includeRenewals ? ' · includes subscription renewals' : ' · excludes subscription renewals')}
          </Typography>
        </Box>
        <Box component="label" sx={{ position: 'relative', flex: '0 1 220px' }}>
          <SearchIcon aria-hidden="true" sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: '#64748B', pointerEvents: 'none' }} />
          <Box
            component="input"
            type="search"
            aria-label="Filter campaigns by name"
            placeholder="Filter campaigns"
            value={q}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQ(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              // Keeps the page's J/K and / shortcuts from firing while typing here.
              e.stopPropagation();
              if (e.key === 'Escape') { setQ(''); e.currentTarget.blur(); }
            }}
            sx={{ ...dateInput, width: '100%', pl: '28px', '&:focus': { outline: 'none', borderColor: '#6366F1', boxShadow: '0 0 0 1px #6366F1' } }}
          />
        </Box>
        </>)}
        {windows && (
          <Box role="group" aria-label="The card's periods" sx={buttonGroup}>
            {(['before', 'after'] as const).map(key => (
              <Box
                key={key}
                component="button"
                type="button"
                aria-pressed={preset === key}
                onClick={() => pickWindow(key, windows[key])}
                sx={pressable(preset === key)}
              >
                {key === 'before' ? 'Before' : 'After'}
                <Box component="span" sx={{ ml: '6px', fontWeight: 500, color: '#64748B' }}>
                  {rangeWords(windows[key], 'to')}
                </Box>
              </Box>
            ))}
          </Box>
        )}
        <Box role="group" aria-label="Date range" sx={buttonGroup}>
          {DATE_PRESETS.map(p => (
            <Box
              key={p.key}
              component="button"
              type="button"
              aria-pressed={preset === p.key}
              onClick={() => setPreset(p.key)}
              sx={pressable(preset === p.key)}
            >
              {p.label}
            </Box>
          ))}
          <Box
            component="button"
            type="button"
            aria-pressed={preset === 'custom'}
            onClick={() => setPreset('custom')}
            sx={pressable(preset === 'custom')}
          >
            Custom
          </Box>
        </Box>
        {preset === 'custom' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              fontSize: '0.8125rem',
              color: '#475569',
            }}
          >
            <Box
              component="input"
              type="date"
              aria-label="From"
              value={custom.from}
              max={today}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                e.target.value &&
                setCustom(c => ({ ...c, from: e.target.value }))
              }
              sx={dateInput}
            />
            to
            <Box
              component="input"
              type="date"
              aria-label="To"
              value={custom.to}
              max={today}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                e.target.value && setCustom(c => ({ ...c, to: e.target.value }))
              }
              sx={dateInput}
            />
          </Box>
        )}
        {renewalsToChoose && (
          <Box sx={buttonGroup}>
            <Box
              component="button"
              type="button"
              aria-pressed={includeRenewals}
              onClick={() => setIncludeRenewals(on => !on)}
              sx={pressable(includeRenewals)}
            >
              With subscriptions
            </Box>
          </Box>
        )}
        {!inRow && (
          <Box sx={buttonGroup}>
            <Box
              component="button"
              type="button"
              onClick={() => setPickerOpen(true)}
              sx={pressable(false)}
            >
              Columns
            </Box>
          </Box>
        )}
      </Box>
      {preset === 'custom' && custom.from > custom.to && (
        <Alert severity="warning" sx={{ mx: 1.75, mb: 1.25 }}>
          The start date is after the end date.
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{ mx: 1.75, mb: 1.25 }}
          action={
            <Button onClick={() => load({ cancelled: false })}>Retry</Button>
          }
        >
          Campaign numbers could not be loaded: {error}
        </Alert>
      )}
      <Box
        sx={{
          maxHeight: inRow ? 360 : 560,
          overflow: 'auto',
          borderTop: RULE,
          position: 'relative',
          opacity: loading && rows ? 0.6 : 1,
          transition: 'opacity 150ms',
        }}
      >
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
            fontSize: '0.8125rem',
            fontVariantNumeric: 'tabular-nums',
            '& th, & td': {
              px: 1.25,
              py: 0.75,
              textAlign: 'right',
              whiteSpace: 'nowrap',
              borderBottom: RULE,
            },
          }}
        >
          <thead>
            <tr>
              {headerCell(
                'name',
                'Campaign',
                'Campaign, then its ad groups and ads',
                true,
              )}
              {visibleColumns.map(column =>
                headerCell(
                  column.key,
                  column.label,
                  `${column.description} (${SOURCE_LABEL[column.source]})`,
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {loading && !rows && (
              <tr>
                <Box
                  component="td"
                  colSpan={visibleColumns.length + 1}
                  sx={{
                    textAlign: 'left !important',
                    color: '#64748B',
                    py: '16px !important',
                  }}
                >
                  Loading campaign numbers…
                </Box>
              </tr>
            )}
            {empty && (
              <tr>
                <Box
                  component="td"
                  colSpan={visibleColumns.length + 1}
                  sx={{
                    textAlign: 'left !important',
                    color: '#475569',
                    py: '16px !important',
                    whiteSpace: 'normal !important',
                  }}
                >
                  {inRow ? (
                    'No Google Ads spend for this campaign in this range.'
                  ) : q.trim() ? (
                    `No campaign name contains "${q.trim()}".`
                  ) : mine && tree.length ? (
                    mineEmptyNote(mine, range)
                  ) : (
                    'No Google Ads spend in this range.'
                  )}
                </Box>
              </tr>
            )}
            {shown.map(renderRow)}
          </tbody>
          {total && (
            <tfoot>
              <Box
                component="tr"
                sx={{
                  '& td': {
                    position: 'sticky',
                    bottom: 0,
                    bgcolor: '#F8FAFC',
                    fontWeight: 700,
                    borderTop: RULE,
                  },
                }}
              >
                <Box
                  component="td"
                  sx={{
                    left: 0,
                    zIndex: 2,
                    textAlign: 'left !important',
                    pl: '10px !important',
                  }}
                >
                  Total · {shown.length}{' '}
                  {shown.length === 1 ? 'campaign' : 'campaigns'}
                </Box>
                {visibleColumns.map(column => (
                  <Box component="td" key={column.key}>
                    {formatPerf(
                      column.format,
                      column.value(total),
                      currencyPrefix(shown[0].store),
                    )}
                  </Box>
                ))}
              </Box>
            </tfoot>
          )}
        </Box>
      </Box>
      {!inRow && <ColumnPicker
        open={pickerOpen}
        columns={columns}
        onClose={() => setPickerOpen(false)}
        onChange={setColumnsAndSave}
      />}
    </Box>
  );
}
